import React, { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EnquiryDialog from "@/components/EnquiryDialog";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import LandingPage from "@/pages/LandingPage";
import Admin from "@/pages/Admin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_PATH = "/manage-vcs-9f3k";

const PaymentOverlay = ({ state, onClose }) => {
  if (!state) return null;
  const map = {
    checking: { icon: <Loader2 className="h-12 w-12 animate-spin text-vivid-cyan" />, title: "Confirming your payment…", text: "Please wait while we confirm your Vivid cruise booking." },
    success: { icon: <CheckCircle2 className="h-12 w-12 text-green-400" />, title: "Booking confirmed! 🎉", text: "Thank you — your Vivid Cruises Sydney tickets are confirmed. A confirmation email is on its way. Get ready for an unforgettable night on Sydney Harbour!" },
    failed: { icon: <XCircle className="h-12 w-12 text-red-400" />, title: "Payment not completed", text: "Your payment wasn't completed. No charge was made — please try booking again or send us an enquiry." },
    timeout: { icon: <XCircle className="h-12 w-12 text-yellow-400" />, title: "Still processing", text: "We couldn't confirm your payment status yet. If you completed payment, you'll receive a confirmation email shortly." },
  };
  const cfg = map[state] || map.checking;
  return (
    <div data-testid="payment-overlay" className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-5 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-vivid-surface p-8 text-center">
        <div className="flex justify-center">{cfg.icon}</div>
        <h2 className="mt-5 font-heading text-2xl font-bold text-white">{cfg.title}</h2>
        <p className="mt-3 text-sm leading-relaxed text-white/70">{cfg.text}</p>
        {state !== "checking" && (
          <button data-testid="payment-overlay-close" onClick={onClose} className="mt-6 rounded-full bg-vivid-magenta px-8 py-3 font-medium text-white transition hover:bg-vivid-magentaHover">
            {state === "success" ? "Explore more" : "Close"}
          </button>
        )}
      </div>
    </div>
  );
};

function PublicShell() {
  const [payState, setPayState] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPayState("checking");
    let attempts = 0;
    const maxAttempts = 6;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const { payment_status, status } = res.data;
        if (payment_status === "paid") { setPayState("success"); return; }
        if (status === "expired") { setPayState("failed"); return; }
        attempts += 1;
        if (attempts >= maxAttempts) { setPayState("timeout"); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        attempts += 1;
        if (attempts >= maxAttempts) setPayState("timeout");
        else setTimeout(poll, 2000);
      }
    };
    poll();
  }, []);

  const closeOverlay = () => {
    setPayState(null);
    window.history.replaceState({}, "", window.location.pathname);
  };

  return (
    <div className="App min-h-screen bg-vivid-base">
      <Navbar />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/:slug" element={<LandingPage />} />
      </Routes>
      <Footer />
      <EnquiryDialog />
      <PaymentOverlay state={payState} onClose={closeOverlay} />
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  if (location.pathname.startsWith(ADMIN_PATH)) {
    return (
      <Routes>
        <Route path={ADMIN_PATH} element={<Admin />} />
      </Routes>
    );
  }
  return <PublicShell />;
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppShell />
      <Toaster position="top-center" richColors theme="dark" />
    </BrowserRouter>
  );
}

export default App;
